/**
 * App Config Store
 *
 * Global state for platform-level app configuration
 * Used for logo, app name, tagline across the app
 */

import { create } from 'zustand';
import type { AppConfigDTO } from '@features/app-config/application/dto/AppConfigDTO';
import { DEFAULT_APP_CONFIG } from '@features/app-config/application/dto/AppConfigDTO';

interface AppConfigState {
  config: AppConfigDTO | null;
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setConfig: (config: AppConfigDTO) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearConfig: () => void;
  fetchConfig: () => Promise<void>;
}

/**
 * Helper to update favicon dynamically
 */
function updateFavicon(faviconUrl?: string) {
  if (!faviconUrl) return;

  // Find existing favicon link
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;

  if (link) {
    link.href = faviconUrl;
  } else {
    // Create new favicon link
    link = document.createElement('link');
    link.rel = 'icon';
    link.href = faviconUrl;
    document.head.appendChild(link);
  }
}

/**
 * Helper to update document title
 */
function updateDocumentTitle(appName?: string) {
  if (!appName) return;

  // Get current title parts
  const currentTitle = document.title;
  const separator = ' - ';

  // If title has separator, replace the app name part
  if (currentTitle.includes(separator)) {
    const parts = currentTitle.split(separator);
    parts[parts.length - 1] = appName;
    document.title = parts.join(separator);
  } else {
    // Set base title
    document.title = appName;
  }
}

export const useAppConfigStore = create<AppConfigState>(set => ({
  config: null,
  isLoaded: false,
  isLoading: false,
  error: null,

  setConfig: config => {
    set({ config, isLoaded: true, isLoading: false, error: null });

    // Update favicon dynamically
    if (config.logoFaviconUrl) {
      updateFavicon(config.logoFaviconUrl);
    }

    // Update document title
    if (config.appName) {
      updateDocumentTitle(config.appName);
    }
  },

  setLoading: loading => set({ isLoading: loading }),

  setError: error => set({ error, isLoading: false }),

  clearConfig: () => set({ config: null, isLoaded: false }),

  fetchConfig: async () => {
    set({ isLoading: true });
    try {
      const { SupabaseAppConfigRepository } = await import(
        '@features/app-config/infrastructure/repositories/SupabaseAppConfigRepository'
      );
      const { GetAppConfigUseCase } = await import(
        '@features/app-config/application/use-cases/GetAppConfigUseCase'
      );
      const repository = new SupabaseAppConfigRepository();
      const useCase = new GetAppConfigUseCase(repository);
      const configDTO = await useCase.execute();

      set({ config: configDTO, isLoaded: true, isLoading: false, error: null });

      // Update favicon and title
      if (configDTO.logoFaviconUrl) {
        updateFavicon(configDTO.logoFaviconUrl);
      }
      if (configDTO.appName) {
        updateDocumentTitle(configDTO.appName);
      }
    } catch (err) {
      console.error('Failed to fetch app config:', err);
      set({ error: err instanceof Error ? err.message : 'Error al cargar configuracion', isLoading: false });
    }
  },
}));

/**
 * Selectors for accessing specific config values
 */
export const appConfigSelectors = {
  selectConfig: (state: AppConfigState) => state.config,
  selectAppName: (state: AppConfigState) => state.config?.appName ?? DEFAULT_APP_CONFIG.appName,
  selectAppTagline: (state: AppConfigState) =>
    state.config?.appTagline ?? DEFAULT_APP_CONFIG.appTagline,
  // Light mode logos
  selectLogoSmall: (state: AppConfigState) => state.config?.logoSmallUrl,
  selectLogoMedium: (state: AppConfigState) => state.config?.logoMediumUrl,
  selectLogoLarge: (state: AppConfigState) => state.config?.logoLargeUrl,
  selectLogoFavicon: (state: AppConfigState) => state.config?.logoFaviconUrl,
  // Dark mode logos
  selectLogoDarkSmall: (state: AppConfigState) => state.config?.logoDarkSmallUrl,
  selectLogoDarkMedium: (state: AppConfigState) => state.config?.logoDarkMediumUrl,
  selectLogoDarkLarge: (state: AppConfigState) => state.config?.logoDarkLargeUrl,
  selectLogoDarkFavicon: (state: AppConfigState) => state.config?.logoDarkFaviconUrl,
  selectPrimaryColor: (state: AppConfigState) =>
    state.config?.primaryColor ?? DEFAULT_APP_CONFIG.primaryColor,
  selectIsLoaded: (state: AppConfigState) => state.isLoaded,
  selectIsLoading: (state: AppConfigState) => state.isLoading,
  selectError: (state: AppConfigState) => state.error,
};
